/**
 * Analytics Input Component
 * Wrapper for Input component with automatic event tracking
 */

import * as React from "react"
import { Input, type InputProps } from "@/shared/widgets/input"
import { useAnalytics } from "../hooks/useAnalytics"
import type { AnalyticsEventProviderType } from "../../application/services/AnalyticsService"

export interface AnalyticsInputProps extends InputProps {
  /**
   * Event name to track on focus
   */
  focusEventName?: string
  /**
   * Event name to track on blur
   */
  blurEventName?: string
  /**
   * Event name to track on change (use with caution - can be noisy)
   */
  changeEventName?: string
  /**
   * Additional properties to include with events
   */
  eventProperties?: Record<string, unknown>
  /**
   * Whether to track events
   * @default true
   */
  trackEvent?: boolean
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
}

const AnalyticsInput = React.forwardRef<HTMLInputElement, AnalyticsInputProps>(
  ({ 
    focusEventName, 
    blurEventName, 
    changeEventName,
    eventProperties, 
    trackEvent = true,
    eventProviders,
    onFocus,
    onBlur,
    onChange,
    ...props 
  }, ref) => {
    const { analytics, isInitialized } = useAnalytics()

    const track = (eventName: string, additionalProps?: Record<string, unknown>) => {
      if (trackEvent && analytics && isInitialized) {
        try {
          analytics.trackEvent(eventName, {
            ...eventProperties,
            ...additionalProps,
            input_name: props.name || props.id || 'unknown',
            input_type: props.type || 'text',
          }, { eventProviders })
        } catch (error) {
          console.error(`Error tracking event "${eventName}":`, error)
        }
      }
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (focusEventName) {
        track(focusEventName)
      }
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (blurEventName) {
        track(blurEventName, { value: e.target.value })
      }
      onBlur?.(e)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (changeEventName) {
        track(changeEventName, { value: e.target.value })
      }
      onChange?.(e)
    }

    return (
      <Input
        ref={ref}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        {...props}
      />
    )
  }
)
AnalyticsInput.displayName = "AnalyticsInput"

export { AnalyticsInput }

